import { computed } from "vue";
import { useNamespacedState } from "vuex-composition-helpers";
import useVuelidate from "@vuelidate/core";
import {
  email,
  helpers,
  maxLength,
  required,
  url,
} from "@vuelidate/validators";

export const ENDERECO_FIELDS = [
  "cep",
  "logradouro",
  "numero",
  "complemento",
  "bairro",
  "cidade_id",
];

const atPath = (object, path) =>
  path.split(".").reduce((value, key) => value?.[key], object);

export function useConcedenteForm(fields) {
  const { concedente } = useNamespacedState("concedentes", ["concedente"]);
  const v$ = useVuelidate(
    computed(() => {
      const rules = concedenteRules(concedente.value);

      return {
        concedente: Object.fromEntries(
          fields.map((field) => [field, rules[field]])
        ),
      };
    }),
    { concedente },
    { $autoDirty: true, $stopPropagation: true }
  );

  const hasErrors = computed(() => v$.value.$error);

  function fieldValidation(path) {
    return atPath(v$.value.concedente, path);
  }

  function error(path) {
    return fieldValidation(path)?.$errors?.[0]?.$message || "";
  }

  function state(path) {
    if (error(path)) return false;

    return fieldValidation(path)?.$dirty ? true : null;
  }

  function isValid() {
    return v$.value.$validate();
  }

  return { concedente, v$, error, state, isValid, hasErrors };
}

const message = helpers.withMessage;
export function concedenteRules(value) {
  const contacts = {};
  for (const field of ["emails", "telefones"]) {
    contacts[field] = Object.fromEntries(
      (value.contatos?.[field] ?? []).map((item, index) => [
        index,
        {
          value: {
            maxLength: message(
              `Use no máximo ${field === "emails" ? 255 : 30} caracteres.`,
              maxLength(field === "emails" ? 255 : 30)
            ),
            ...(field === "emails"
              ? {
                  email: message(
                    "Informe um e-mail institucional válido.",
                    email
                  ),
                }
              : {}),
          },
        },
      ])
    );
  }
  return {
    nome: { required: message("Informe o nome do concedente.", required) },
    tax_id: {
      required: message("Campo obrigatório", required),
      maxLength: message("Use no máximo 50 caracteres.", maxLength(50)),
    },
    concedente_tipo_id: {
      required: message("Selecione a origem do concedente.", required),
    },
    sobre: {
      maxLength: message("Use no máximo 5.000 caracteres.", maxLength(5000)),
    },
    site: {
      maxLength: message("Use no máximo 255 caracteres.", maxLength(255)),
      url: message(
        "Informe um site válido, com ou sem http:// ou https://.",
        (input) => {
          const site = input?.trim() || null;
          const hasProtocol = /^[a-z][a-z\d+.-]*:/i.test(site || "");

          return url.$validator(
            !site || hasProtocol ? site : `https://${site}`
          );
        }
      ),
    },
    endereco: Object.fromEntries(
      ENDERECO_FIELDS.map((field) => [
        field,
        field === "cidade_id"
          ? {
              integer: message(
                "Selecione uma cidade válida.",
                (input) =>
                  input == null ||
                  (Number.isInteger(Number(input)) && Number(input) > 0)
              ),
            }
          : {},
      ])
    ),
    contatos: contacts,
  };
}
